import {
  isCircleInCircle,
  isPointInCircle,
  isDefined,
  isEqual,
  cartesian2polar,
  isTrue,
  FLATCOLOR,
  STEEPCOLOR,
  MAPSTYLES
} from "../shared/index";
import { TOPOGRAPHYEVENTS } from "../shared/events";
import { mapReference } from "../dataGetters/mapValues";
import EventEmitter from "eventemitter3";
import { logStdout } from "../../shared/utils";
// 可移动圆的初始化参数
function initMovableCircle(radius) {
  return {
    center: { x: 0, y: 0 },
    radius: radius,
    start: { x: 0, y: 0 },
    end: { x: 0, y: 0 }
  };
}
function isDataValid(v) {
  return !isEqual(v, -1000, 0.0001);
}
// 初始化圆区域
function initMapCicle(padding, width, height) {
  let drawArea = {}; // 方形绘图区
  drawArea.width = drawArea.height = Math.min(width, height);
  drawArea.left = (width - drawArea.width) / 2.0;
  drawArea.top = (height - drawArea.height) / 2.0;
  let mapCircle = {
    left: drawArea.left + padding.left,
    top: drawArea.top + padding.top,
    width: drawArea.width - padding.left * 2.0
  };
  mapCircle.height = mapCircle.width;
  mapCircle.radius = mapCircle.width / 2.0;
  mapCircle.center = {
    x: mapCircle.left + mapCircle.width / 2.0,
    y: mapCircle.top + mapCircle.height / 2.0
  };
  return mapCircle;
}
// 初始化测量线
function initMeasureLine() {
  return { start: { x: 0, y: 0 }, end: { x: 0, y: 0 }, length: 0 };
}
// 初始化旋转线
function initRotateLine(mapValues) {
  if (!isDefined(mapValues) || !isDefined(mapValues.AxisFlat)) {
    return null;
  }
  const { AxisFlat } = mapValues;

  let angle = !isDataValid(AxisFlat.value) ? -45 : -AxisFlat.value;
  return {
    start: { x: 0, y: 0 },
    endArc: (angle / 180) * Math.PI,
    rotateArc: 0,
    angle
  };
}
// 初始化Canvas大小
function initCanvasSize(width, height) {
  return { width: width, height: height };
}

// RGP绘制方式
const RGPDRAWMODES = {
  select: "select",
  measure: "measure",
  move: "move",
  rotate: "rotate"
};
let rgpDrawMode = "select";

// 计算线长
function calcLineLength(start, end, scale = 1) {
  return Math.sqrt(Math.pow(start.x - end.x, 2) + Math.pow(start.y - end.y, 2)) * scale;
}
// Map绘制类
export default class Map extends EventEmitter {
  constructor(canvas, diameter, specs, mapDrawSettings, movableCircleR, mapstyle) {
    super();
    this.isRGP = specs.isRGP;
    this.isZernike = specs.isZernike;
    this.mapValues = specs.mapValues;
    this.maxPeak = specs.maxPeak;
    this.eyetype = specs.eyetype;
    this.colorMapPtr = specs.colorMapPtr;
    this.originMapCanvas = specs.originMapCanvas;
    this._canvas = canvas;
    this._originalData = specs.data;
    this._canvas.width = this._canvas.getBoundingClientRect().width;
    this._canvas.height = this._canvas.getBoundingClientRect().height;
    this._mapStyle = mapstyle;
    this.canvasSize = initCanvasSize(this._canvas.width, this._canvas.height);
    this._ctx = canvas.getContext("2d");
    this._padding = {
      left: this.canvasSize.width > 700 ? 70 : 50,
      top: this.canvasSize.width > 700 ? 50 : 30
    };
    this._ctx.fillStyle = "white";
    this._ctx.strokeStyle = "white";

    this.mapCircle = initMapCicle(this._padding, this.canvasSize.width, this.canvasSize.height);
    this.rotateLine = initRotateLine(this.mapValues);
    this.measureLine = initMeasureLine();
    this.mapDrawSettings = mapDrawSettings || {};
    this._ctx.save("clear");
    this.calcPixelsPerMM(diameter);
    this._isMouseDownBoolen = false;

    this._measureLines = [];
    this.movableCircle = initMovableCircle(this._dataInMap(movableCircleR));

    this._numericValues = [];
    this._initNumericVals();
    this._minMaxValues = [];
    this._initminMaxVals();
    this._pupilEdgeValues = [];
    this._initPupilEdgeVals();

    this.on(TOPOGRAPHYEVENTS.onDrawStart, (e) => {
      this._isMouseDownBoolen = true;
      this._drawTick2ColorBar(e);
      this._RGPMousedown(e);
    });
    // this._canvas.onmousedown = e => {
    //   this._isMouseDownBoolen = true
    //   this._drawTick2ColorBar(e)
    //   this._RGPMousedown(e)
    // }
    this.on(TOPOGRAPHYEVENTS.onDrawMove, (e) => {
      this.updateMapDraw(e);
      this._drawTick2ColorBar(e);
    });
    // this._canvas.onmousemove = e => {
    //   this.updateMapDraw(e)
    //   this._drawTick2ColorBar(e)
    // }
    this.on(TOPOGRAPHYEVENTS.onDrawEnd, (e) => {
      this._canvas.style.cursor = "default";
      this._RGPMouseup(e);
      this._drawTick2ColorBarMouseUp(e);
      this.updateMapDraw(e);
      this._isMouseDownBoolen = false;
    });
    // this._canvas.onmouseup = e => {
    //   this._canvas.style.cursor = 'default'
    //   this._RGPMouseup(e)
    //   this._drawTick2ColorBarMouseUp(e)
    //   this._isMouseDownBoolen = false
    // }
    this.on(TOPOGRAPHYEVENTS.rgpDrawModeChanged, (mode) => {
      this._measureLines = [];
      rgpDrawMode = mode;
      this.updateMapDraw();
    });
  }
  // 获取绘制模式
  getRGPDrawMode() {
    return rgpDrawMode;
  }

  // 初始化数据绘制
  _initNumericVals() {
    let vals = this._originalData.numericVals;
    if (isDefined(vals)) {
      let fix = this._mapStyle === MAPSTYLES[5] ? 0 : 2;
      this._numericValues = this._normalPoints(true, vals.px, vals.py, vals.pz, fix);
    }
  }

  // 初始化瞳孔
  _initPupilEdgeVals() {
    let vals = this._originalData.pupilEdgeVals;
    if (isDefined(vals)) {
      this._pupilEdgeValues = this._normalPoints(false, vals.px, vals.py);
    }
  }

  // 初始化最大最小值得
  _initminMaxVals() {
    let vals = this._originalData.minMaxVals;
    this._minMaxValues = [];
    if (isDefined(vals)) {
      vals.px.map((item, index) => {
        if (
          isDataValid(item) &&
          isDefined(vals.py) &&
          isDefined(vals.pz) &&
          isDataValid(vals.pz[index])
        ) {
          this._minMaxValues.push({
            x: item,
            y: vals.py[index],
            value: isDataValid(vals.pz[index]) ? vals.pz[index]._toFixed(1) : ""
          });
        }
      });
    }
  }

  // 绘制取值
  _drawTick2ColorBar(e) {
    if (
      e.which === 1 &&
      (!this.isRGP || rgpDrawMode === RGPDRAWMODES.select) &&
      this._isMouseDownBoolen
    ) {
      let x = (e.offsetX - this.mapCircle.center.x) * this._mmPerPixel;
      let y = -(e.offsetY - this.mapCircle.center.y) * this._mmPerPixel;

      this._drawTick(e, {
        x: x,
        y: y
      });
      this.emit(TOPOGRAPHYEVENTS.drawTickColorBar, { x: x, y: y });
    }
  }

  // 绘制colorbar选值
  _drawTick2ColorBarMouseUp(e) {
    if (
      e.which === 1 &&
      (!this.isRGP || rgpDrawMode === RGPDRAWMODES.select) &&
      this._isMouseDownBoolen
    ) {
      this.emit(TOPOGRAPHYEVENTS.finishDrawTick);
    }
  }

  // RGP绘制mousedown事件
  _RGPMousedown(e) {
    if (this.isRGP) {
      if (rgpDrawMode === RGPDRAWMODES.move) {
        this._isMouseDownBoolen = this._initMovableCicleMouseDown(e);
      } else if (rgpDrawMode === RGPDRAWMODES.rotate) {
        this._isMouseDownBoolen = this._initRoateLineMouseDown(e);
      } else if (rgpDrawMode === RGPDRAWMODES.measure) {
        this._isMouseDownBoolen = this._initMeasureLineMouseDown(e);
      }
    }
  }

  // RGP绘制MouseUp事件
  _RGPMouseup(e) {
    if (this.isRGP) {
      if (rgpDrawMode === RGPDRAWMODES.move) {
        this._finishMovableCicleMouseUp();
      } else if (rgpDrawMode === RGPDRAWMODES.rotate) {
        this._finishRotateMouseUp();
      } else if (rgpDrawMode === RGPDRAWMODES.measure) {
        this._finishMeasureLineMouseUp(e);
      }
    }
  }

  // 判断鼠标落点在圆内
  _isPointMouseDownPosInCircle(e) {
    return isPointInCircle(
      e.offsetX - this.mapCircle.center.x,
      e.offsetY - this.mapCircle.center.y,
      this.mapCircle.radius
    );
  }

  // 初始化测量线Mousedown事件
  _initMeasureLineMouseDown(e) {
    if (this._isPointMouseDownPosInCircle(e)) {
      this.measureLine = initMeasureLine();
      this.measureLine.start.x = e.offsetX - this.mapCircle.center.x;
      this.measureLine.start.y = e.offsetY - this.mapCircle.center.y;
      return this.isRGP;
    } else {
      return false;
    }
  }

  // 测量线绘制结束
  _finishMeasureLineMouseUp(e) {
    if (this._isMouseDownBoolen) {
      this.measureLine.end.x = e.offsetX - this.mapCircle.center.x;
      this.measureLine.end.y = e.offsetY - this.mapCircle.center.y;
      this.measureLine.length = calcLineLength(
        this.measureLine.start,
        this.measureLine.end,
        this._mmPerPixel
      )._toFixed(2);
      if (
        Math.pow(this.measureLine.end.x - this.movableCircle.center.x, 2) +
          Math.pow(this.measureLine.end.x - this.movableCircle.center.y, 2) <=
        Math.pow(this.movableCircle.radius, 2)
      ) {
        this._measureLines.push(this.measureLine);
      }
    }
    this._isMouseDownBoolen = false;
  }

  // 初始化移动圆Mousedown坐标值
  _initMovableCicleMouseDown(e) {
    if (!this.isRGP) return;
    if (this._isPointMouseDownPosInCircle(e)) {
      this.movableCircle.start.x = e.offsetX;
      this.movableCircle.start.y = e.offsetY;
      this._canvas.style.cursor = "move";
      return this.isRGP;
    } else {
      return false;
    }
  }

  // 圆形移动完成
  _finishMovableCicleMouseUp() {
    this.movableCircle.end.x = this.movableCircle.center.x;
    this.movableCircle.end.y = this.movableCircle.center.y;
    this.emit(TOPOGRAPHYEVENTS.rgpOffsetChanged, {
      offsetX: this.movableCircle.center.x * this._mmPerPixel,
      offsetY: -this.movableCircle.center.y * this._mmPerPixel
    });
    this._isMouseDownBoolen = false;
  }

  // 初始化旋转线
  _initRoateLineMouseDown(e) {
    if (!this.isRGP) return;
    if (this._isPointMouseDownPosInCircle(e)) {
      // this.rotateLine.rotateArc = this.rotateLine.endArc
      this.rotateLine.start.x = e.offsetX;
      this.rotateLine.start.y = e.offsetY;
      return this.isRGP;
    } else {
      return false;
    }
  }

  // 完成旋转线
  _finishRotateMouseUp(e) {
    if (!this.isRGP) return;
    this.rotateLine.endArc += this.rotateLine.rotateArc;
    this.rotateLine.rotateArc = 0;

    let angle = -(this.rotateLine.endArc * 180) / Math.PI;
    angle %= 360;
    if (angle < 0) angle = 360 + angle;
    if (angle > 360) angle = angle - 360;
    this.emit(TOPOGRAPHYEVENTS.rgpRotate, angle);
    this._isMouseDownBoolen = false;
  }

  // 设置显示项设置
  setMapdrawSettings(settings) {
    this.mapDrawSettings = settings.mapDrawSettings;
    this.dataShowSettings = settings.dataShowSettings;
    this.updateMapDraw();
  }

  // 更新绘制
  updateMapDraw(mvevent) {
    this._clearDraw();
    this._ctx.translate(this.mapCircle.center.x, this.mapCircle.center.y);

    // 中心线-测试用
    // this.testCenter()

    this._ctx.fillStyle = "white";
    this._ctx.strokeStyle = "white";
    let fontsize = this._canvasWidth > 700 ? 15 : 16;
    this._ctx.font = `${fontsize}px arial`;

    this._drawCircleScale();
    this._drawXYAxis();
    this._drawShaps();
    // RGP Reference
    this._drawRotateLine(mvevent);
    this._paintMovebableCircle(mvevent);
    this._drawMeasureLine(mvevent);
  }
  drawCircleScale() {
    this._drawCircleScale();
  }
  // 绘制带箭头线
  _drawArrowLine(fromX, fromY, toX, toY) {
    var headlen = 5; // 自定义箭头线的长度
    var theta = 45; // 自定义箭头线与直线的夹角，个人觉得45°刚刚好
    var arrowX, arrowY; // 箭头线终点坐标
    // 计算各角度和对应的箭头终点坐标
    var angle = (Math.atan2(fromY - toY, fromX - toX) * 180) / Math.PI;
    var angle1 = ((angle + theta) * Math.PI) / 180;
    var angle2 = ((angle - theta) * Math.PI) / 180;
    var topX = headlen * Math.cos(angle1);
    var topY = headlen * Math.sin(angle1);
    var botX = headlen * Math.cos(angle2);
    var botY = headlen * Math.sin(angle2);
    // 画直线
    this._ctx.lineWidth = 2;
    this._ctx.beginPath();
    this._ctx.strokeStyle = "white";
    this._ctx.moveTo(fromX, fromY);
    this._ctx.lineTo(toX, toY);
    this._ctx.stroke();

    this._ctx.beginPath();
    this._ctx.strokeStyle = "black";
    this._ctx.save("dashline");

    this._ctx.setLineDash([10, 8]);
    this._ctx.moveTo(fromX, fromY);
    this._ctx.lineTo(toX, toY);
    this._ctx.stroke();
    this._ctx.restore("dashline");
    this._ctx.beginPath();
    arrowX = toX + topX;
    arrowY = toY + topY;
    // 画上边箭头线
    this._ctx.moveTo(arrowX, arrowY);
    this._ctx.lineTo(toX, toY);

    arrowX = toX + botX;
    arrowY = toY + botY;
    // 画下边箭头线
    this._ctx.lineTo(arrowX, arrowY);
    this._ctx.stroke();
  }

  // 绘制箭头测量线
  _drawMeasueArrowLine(line) {
    this._ctx.beginPath();
    this._ctx.strokeStyle = "white";
    this._drawArrowLine(line.start.x, line.start.y, line.end.x, line.end.y);
    this._ctx.fillStyle = "white";
    let txt = `${line.length}mm`;
    this._ctx.font = `16px arial`;
    this._ctx.fillText(txt, (line.start.x + line.end.x) / 2, (line.start.y + line.end.y) / 2);
  }

  // 绘制测量线
  _drawMeasureLine(e) {
    this._drawMeasureLines();
    if (!this.isRGP) return;
    if (rgpDrawMode !== RGPDRAWMODES.measure || !isDefined(e)) return;
    if (this._isMouseDownBoolen) {
      this._ctx.save("translate");
      this._ctx.beginPath();
      this._ctx.lineWidth = 2;
      const start = {
        x: this.measureLine.start.x,
        y: this.measureLine.start.y
      };
      const end = {
        x: e.offsetX - this.mapCircle.center.x,
        y: e.offsetY - this.mapCircle.center.y
      };
      let line = {
        start: start,
        end: end,
        length: calcLineLength(start, end, this._mmPerPixel)._toFixed(2)
      };
      this._drawMeasueArrowLine(line);
      this._ctx.restore("translate");
    }
  }

  // 绘制测量线
  _drawMeasureLines() {
    this._ctx.save("translate");
    this._measureLines.map((line) => {
      this._drawMeasueArrowLine(line);
    });
    this._ctx.restore("translate");
  }

  // 绘制旋转线
  _drawRotateLine(e) {
    if (!(rgpDrawMode === RGPDRAWMODES.rotate) || !this.isRGP) return;
    this._ctx.save("translate");
    this._ctx.translate(this.movableCircle.center.x, this.movableCircle.center.y);
    if (isDefined(e) && this._isMouseDownBoolen) {
      let pointToMovableEnd = {
        x: e.offsetX - this.mapCircle.center.x - this.movableCircle.center.x,
        y: e.offsetY - this.mapCircle.center.y - this.movableCircle.center.y
      };
      let pointToMovableStart = {
        x: this.rotateLine.start.x - this.mapCircle.center.x - this.movableCircle.center.x,
        y: this.rotateLine.start.y - this.mapCircle.center.y - this.movableCircle.center.y
      };
      let endAngle = Math.atan2(pointToMovableEnd.y, pointToMovableEnd.x);
      let startAngle = Math.atan2(pointToMovableStart.y, pointToMovableStart.x);
      this.rotateLine.rotateArc = endAngle - startAngle;
      const angleSum = this.rotateLine.endArc + this.rotateLine.rotateArc;
      logStdout("rotate angle", (this.rotateLine.rotateArc * 180) / Math.PI);
      logStdout(
        "rotate line start  and end angle",
        (startAngle * 180) / Math.PI,
        (endAngle * 180) / Math.PI
      );
      this._ctx.rotate(angleSum);
    } else {
      // logStdout('mouse up end angle', this.rotateLine.endArc * 180 / Math.PI)
      this._ctx.rotate(this.rotateLine.endArc);
    }
    // this.testCenter()
    // this._ctx.rotate(this.rotateLine.rotateArc)
    // 绘制顶部小圆
    this._ctx.beginPath();
    // this.testCenter()
    // 绘制实线
    let x = this.movableCircle.radius;
    let y = 0;
    const r = 6;
    this._ctx.beginPath();
    this._ctx.arc(x, y, r, 0, Math.PI * 2);
    this._ctx.fill();

    // 绘制圆心到顶部小圆的直线
    this._ctx.lineWidth = 4;
    this._ctx.beginPath();
    this._ctx.moveTo(0, 0);
    this._ctx.lineTo(x, y);
    this._ctx.stroke();

    // 绘制底部小圆
    x = 0 - x;
    this._ctx.beginPath();
    this._ctx.arc(x, y, r, 0, Math.PI * 2);
    this._ctx.fill();

    // 实线
    this._ctx.save("draw-dotted-line");
    this._ctx.beginPath();
    this._ctx.strokeStyle = "black";
    this._ctx.moveTo(0, 0);
    this._ctx.lineTo(x, y);
    this._ctx.stroke();
    this._ctx.restore("draw-dotted-line");
    // 虚线
    this._ctx.beginPath();
    this._ctx.setLineDash([10, 8]);
    this._ctx.moveTo(0, 0);
    this._ctx.lineTo(x, y);
    this._ctx.stroke();

    // 绘制中心小圆
    this._ctx.beginPath();
    this._ctx.arc(0, 0, r, 0, Math.PI * 2);
    this._ctx.fill();
    this._ctx.restore("translate");
  }

  // 测试圆中心
  testCenter(color = "red") {
    this._ctx.strokeStyle = color;
    this._ctx.beginPath();
    // x轴
    this._ctx.moveTo(-this.mapCircle.radius, 0);
    this._ctx.lineTo(this.mapCircle.radius, 0);
    this._ctx.stroke();
    this._ctx.fillText("X轴", this.mapCircle.radius + 10, 0);
    this._ctx.beginPath();
    // y轴
    this._ctx.strokeStyle = "yellow";
    this._ctx.moveTo(0, -this.mapCircle.radius);
    this._ctx.lineTo(0, this.mapCircle.radius);
    this._ctx.fillText("Y轴", 0, this.mapCircle.radius + 10);
    this._ctx.stroke();
  }
  // 计算像素和mm之间的比例尺
  calcPixelsPerMM(diameter) {
    this._realDiameter = diameter;
    this._pixelPerMM = this.mapCircle.width / diameter;
    this._mmPerPixel = 1.0 / this._pixelPerMM;
    this._initNumericVals();
    this._initminMaxVals();
  }

  // 绘制移动移动圆
  _paintMovebableCircle(e) {
    if (this.isRGP && this._isMouseDownBoolen && rgpDrawMode === RGPDRAWMODES.move) {
      this.movableCircle.offset = {
        x: e.offsetX - this.movableCircle.start.x,
        y: e.offsetY - this.movableCircle.start.y
      };
      if (
        isCircleInCircle(
          this.movableCircle.end.x + this.movableCircle.offset.x,
          this.movableCircle.end.y + this.movableCircle.offset.y,
          this.movableCircle.radius,
          this.mapCircle.radius
        )
      ) {
        this.movableCircle.center = {
          x: this.movableCircle.end.x + this.movableCircle.offset.x,
          y: this.movableCircle.end.y + this.movableCircle.offset.y
        };
        this.emit("movable-center-pos", this.movableCircle.offset);
      }
    }
    this._drawMovableCircle();
  }
  // 绘制
  _drawMovableCircle() {
    if (!this.isRGP || !(rgpDrawMode === RGPDRAWMODES.move) || !this._isMouseDownBoolen) {
      return;
    }
    this._ctx.save("translate");
    this._ctx.beginPath();
    this._ctx.lineWidth = 4;
    this._ctx.strokeStyle = "wheat";
    this._ctx.arc(
      this.movableCircle.center.x,
      this.movableCircle.center.y,
      this.movableCircle.radius,
      0,
      2 * Math.PI
    );

    this._ctx.stroke();
    this._ctx.restore("translate");
  }

  // 清理绘制
  _clearDraw() {
    this._ctx.restore("clear");
    this._ctx.clearRect(0, 0, this.canvasSize.width, this.canvasSize.height);
    this._ctx.save("clear");
  }

  // 绘制大圆和轮廓
  _drawCircleScale() {
    this._ctx.save("translate");
    let r = this.mapCircle.radius;
    // 绘制大圆
    this._ctx.beginPath();
    this._ctx.arc(0, 0, r, 0, 2 * Math.PI);
    this._ctx.stroke();
    let w = this._measureText("").width;
    // 绘制圆上的刻度
    this._ctx.rotate(0.5 * Math.PI);
    for (let id = 0; id < 360; id += 15) {
      this._ctx.beginPath();
      if (id % 30 === 0) {
        w = this._measureText(id + "°").width;
        this._ctx.fillText(id + "°", -w / 2, -r - 8);
      }
      this._ctx.moveTo(0, -r);
      this._ctx.lineTo(0, -r - 5);
      this._ctx.stroke();
      this._ctx.rotate((-15 * Math.PI) / 180);
    }
    this._ctx.restore("translate");
  }

  // 绘制XY轴
  _drawXYAxis() {
    this._ctx.save("translate");
    let offsety = this.canvasSize.width > 700 ? 80 : 50;
    let offsetx = this.canvasSize.width > 700 ? 30 : 30;
    let r = this.mapCircle.radius;
    let size = this._measureText("0");
    let w = size.width;
    let h = size.height;
    // y轴刻度
    const scaleSize = 5; // 刻度线长
    let yAxisleft = -r - offsety;
    let text;
    if (!this.isRGP) {
      this._ctx.beginPath();

      // y轴
      this._ctx.moveTo(-r - offsety, -r - offsety + 10);
      this._ctx.lineTo(-r - offsety, r + offsetx);
      this._ctx.stroke();
      this._ctx.beginPath();
      this._ctx.beginPath();
      this._ctx.moveTo(yAxisleft, 0);
      this._ctx.lineTo(yAxisleft - scaleSize, 0);
      this._ctx.stroke();
      w = this._measureText("0").width;

      this._ctx.fillText("0", yAxisleft - scaleSize - w, h / 2);
      // y轴

      for (let id = 1; id <= this._realDiameter / 2; id++) {
        this._ctx.beginPath();
        this._ctx.moveTo(yAxisleft, this._dataInMap(id));
        this._ctx.lineTo(yAxisleft - scaleSize, this._dataInMap(id));
        text = this.dataShowSettings.isDiameter ? `${id * 2}` : `${id}`;
        w = this._measureText(text).width;
        this._ctx.fillText(text, yAxisleft - scaleSize - w, this._dataInMap(id) + h / 2);
        this._ctx.stroke();
        this._ctx.beginPath();
        this._ctx.moveTo(yAxisleft, this._dataInMap(id, true));
        this._ctx.lineTo(yAxisleft - scaleSize, this._dataInMap(id, true));
        this._ctx.fillText(text, yAxisleft - scaleSize - w, this._dataInMap(id, true) + h / 2);
        this._ctx.stroke();
      }

      // x轴

      this._ctx.moveTo(-r - offsetx, r + offsetx);
      this._ctx.lineTo(r + offsetx, r + offsetx);
      this._ctx.stroke();
      // x轴刻度
      let xAxisTop = r + offsetx;
      this._ctx.beginPath();
      this._ctx.moveTo(0, xAxisTop);
      this._ctx.lineTo(0, xAxisTop + scaleSize);
      this._ctx.stroke();
      w = this._measureText("0").width;
      this._ctx.fillText("0", -w / 2, xAxisTop + scaleSize + h * 2);
      for (let id = 1; id <= this._realDiameter / 2; id++) {
        this._ctx.beginPath();
        this._ctx.moveTo(this._dataInMap(id), xAxisTop);
        this._ctx.lineTo(this._dataInMap(id), xAxisTop + scaleSize);
        text = this.dataShowSettings.isDiameter ? `${id * 2}` : `${id}`;
        w = this._measureText(text).width;
        this._ctx.fillText(text, this._dataInMap(id) - w / 2, xAxisTop + scaleSize + h * 2);
        this._ctx.stroke();
        this._ctx.beginPath();
        this._ctx.moveTo(this._dataInMap(id, true), xAxisTop);
        this._ctx.lineTo(this._dataInMap(id, true), xAxisTop + scaleSize);
        this._ctx.fillText(text, this._dataInMap(id, true) - w / 2, xAxisTop + scaleSize + h * 2);
        this._ctx.stroke();
      }
    }
    this._ctx.restore("translate");
  }
  // 绘制网格
  _drawGrid() {
    this._ctx.save("translate");
    this._ctx.strokeStyle = this._ctx.fillStyle = "black";
    let fontsize = this.canvasSize.width > 700 ? 16 : 14;
    this._ctx.font = `${fontsize}px arial`;
    let r = this.mapCircle.radius;
    let text = "";
    let size = this._measureText(text);
    let w = size.width;
    let h = size.height;
    for (let id = 1; id <= this._realDiameter / 2; id++) {
      if (this._dataInMap(id) < r) {
        this._ctx.beginPath();
        // 绘制数字
        text = this.dataShowSettings.isDiameter ? `${id * 2}` : `${id}`;
        size = this._measureText(text);
        w = size.width;
        h = size.height;
        let offsetAngle = Math.atan2(h + 5, this._dataInMap(id));
        this._ctx.fillText(text, this._dataInMap(id, true) - w / 2, h / 2 + 2);
        this._ctx.fillText(text, this._dataInMap(id) - w / 2, h / 2 + 2);

        // 绘制间隔线
        // 左侧
        this._ctx.beginPath();
        this._ctx.moveTo(-w - this._dataInMap(id), 0);
        let end = this._dataInMap(id + 1) - w;
        if (this._dataInMap(id + 1) + w > r) {
          end = r;
        }
        this._ctx.lineTo(-end, 0);
        this._ctx.stroke();
        // 右侧
        this._ctx.beginPath();
        end = this._dataInMap(id + 1) - w;
        if (this._dataInMap(id + 1) - w > r) {
          end = r;
        }
        this._ctx.moveTo(this._dataInMap(id) + w, 0);
        this._ctx.lineTo(end, 0);
        this._ctx.stroke();

        // 绘制弧
        this._ctx.beginPath();
        this._ctx.arc(0, 0, this._dataInMap(id), offsetAngle, Math.PI - offsetAngle);
        this._ctx.stroke();
        this._ctx.beginPath();
        this._ctx.arc(0, 0, this._dataInMap(id), -offsetAngle, Math.PI + offsetAngle, true);
        this._ctx.stroke();
      }
    }
    // 绘制网格线
    for (let id = 0; id < 360; id += 15) {
      this._ctx.beginPath();
      if (id !== 0 && id !== 180) {
        this._ctx.moveTo(this._pixelPerMM, 0);
        this._ctx.lineTo(r, 0);
        this._ctx.stroke();
      }
      this._ctx.rotate((-15 * Math.PI) / 180);
    }
    this._ctx.restore("translate");
  }
  // 绘制最大最小值
  _drawMaxMin() {
    if (this._minMaxValues.length <= 0) return;
    let size = this._measureText("");
    let w = size.width;
    let h = size.height;
    this._ctx.save("translate");

    this._ctx.strokeStyle = this._ctx.fillStyle = "black";
    let fontsize = this.canvasSize.width > 700 ? 16 : 14;
    this._ctx.font = `${fontsize}px arial`;
    let r = this.mapCircle.radius;
    for (let id = 1; id <= this._realDiameter / 2; id++) {
      if (this._dataInMap(id) < r) {
        this._ctx.beginPath();
        let i = id + 0.5;
        let text = (i * 2).toString();
        size = this._measureText(text);
        w = size.width;
        h = size.height;
        let offsetAngle = Math.atan2(h / 2 + 5, this._dataInMap(i));
        this._ctx.fillText(text, this._dataInMap(i, true) - w / 2, h / 2);
        this._ctx.fillText(text, this._dataInMap(i) - w / 2, h / 2);
        // 绘制弧
        this._ctx.beginPath();
        this._ctx.arc(0, 0, this._dataInMap(i), offsetAngle, Math.PI - offsetAngle);
        this._ctx.stroke();
        this._ctx.beginPath();
        this._ctx.arc(0, 0, this._dataInMap(i) - offsetAngle, Math.PI + offsetAngle, true);
        this._ctx.stroke();
      }
    }
    this._drawMaxMinVals(this._minMaxValues);
    this._ctx.restore("translate");
    // this._drawMaxMinVals(this._ctx, 'green', test2)
    // this._drawData(data)
  }
  // 最大最小值的起点
  _calcMaxMinStartPoint(endX, endY, radius) {
    let angle = Math.atan2(endX, endY);
    let r = radius === 0 ? 1.5 : radius + 1;
    let point = {
      start: {
        x: this._dataInMap(radius * Math.cos(Math.PI / 2 - angle)),
        y: this._dataInMap(radius * Math.sin(Math.PI / 2 - angle), true)
      },

      end: {
        x: this._dataInMap(r * Math.cos(Math.PI / 2 - angle)),
        y: this._dataInMap(r * Math.sin(Math.PI / 2 - angle), true)
      },
      isHorizon: Math.abs(angle) < Math.PI / 4 || Math.abs(angle) > (Math.PI * 3) / 4
    };
    return point;
  }

  // 绘制最大最小值
  _drawMaxMinVals(points) {
    this._ctx.beginPath();
    this._ctx.lineWidth = 6;
    let innerR = 0;
    let fontsize = this.canvasSize.width > 700 ? 18 : 13;
    this._ctx.font = `${fontsize}px arial`;
    let r = this.mapCircle.radius;
    let size = this._measureText("");
    let w = size.width;
    let h = size.height;
    let cnt = 0;
    points.map((item, index) => {
      if (index === 0) {
        innerR = 0;
      } else if (index % 4 === 0) {
        innerR = innerR === 0 ? 1.5 : innerR + 1;
      }
      if (cnt === 4) {
        cnt = 0;
      }
      let point = this._calcMaxMinStartPoint(item.x, item.y, innerR);
      if (isPointInCircle(point.end.x, point.end.y, r)) {
        this._ctx.beginPath();
        this._ctx.lineWidth = 8;
        this._ctx.strokeStyle = "black";
        this._ctx.moveTo(point.start.x, point.start.y);
        this._ctx.lineTo(point.end.x, point.end.y);
        this._ctx.stroke();
        this._ctx.beginPath();
        this._ctx.lineWidth = 6;
        this._ctx.strokeStyle = this._getColor(cnt < 2);
        this._ctx.moveTo(point.start.x, point.start.y);
        this._ctx.lineTo(point.end.x, point.end.y);
        this._ctx.stroke();
        let text = item.value;
        if (text && text.length > 0) {
          w = this._measureText(text).width;
          let oy = point.isHorizon ? h + 4 : 0;
          this._ctx.fillStyle = "black";
          this._ctx.fillRect(
            (point.start.x + point.end.x) / 2 - w / 2 - 2,
            (point.start.y + point.end.y) / 2 - h - 4 + oy,
            w + 4,
            h + 8
          );
          this._ctx.fillStyle = this._getColor(cnt < 2);
          this._ctx.fillText(
            text,
            (point.start.x + point.end.x) / 2 - w / 2,
            (point.start.y + point.end.y) / 2 + oy
          );
        }
      }
      cnt++;
    });
  }

  // 获取平k斗K的颜色
  _getColor(flag) {
    return flag ? FLATCOLOR : STEEPCOLOR;
  }
  _measureText(str) {
    let w = this._ctx.measureText(str).width;
    let h = this._ctx.measureText("Z").width;
    return {
      width: w,
      height: h
    };
  }

  // 绘制数值
  _drawNumericValues() {
    if (this._numericValues.length <= 0) return;
    this._ctx.save("translate");
    let sizeCanvas = this.canvasSize.width;
    if (this.canvasSize.width > this.canvasSize.height) {
      sizeCanvas = this.canvasSize.height;
    }
    let fontsize = !this.isRGP && sizeCanvas > 700 ? 20 : 13;
    this._ctx.font = `${fontsize}px arial`;
    this._ctx.fillStyle = "black";
    let r = this.mapCircle.radius;
    let size = this._measureText("");
    let w = size.width;
    let h = size.height;
    this._numericValues.map((item, index) => {
      w = this._measureText(item.value).width;
      if (isPointInCircle(item.x, item.y, r) && this.originMapCanvas) {
        const bright = 0.299 * item.rgb[0] + 0.587 * item.rgb[1] + 0.114 * item.rgb[2];
        const BRIGHT = 127;
        if (bright > BRIGHT) {
          this._ctx.fillStyle = "#000000";
        } else {
          // this._ctx.fillStyle = `rgb(${255 - imageData[0]},${255 - imageData[1]}, ${255 - imageData[2]})`
          this._ctx.fillStyle = "#ffffff";
        }
        this._ctx.fillText(item.value, item.x - w / 2.0, item.y + h / 2.0);
      }
    });
    this._ctx.fillStyle = "white";
    this._ctx.restore("translate");
  }

  // 格式化数据点坐标
  _normalPoints(flag, pointsX, pointsY, pointsZ = null, fixed = 1) {
    const scale = (600 * 1.0) / 12.0;
    let _points = [];
    let pointsLen = pointsZ.length;
    for (let i = 0; i < pointsLen; i++) {
      if (isDataValid(pointsX[i]) && isDataValid(pointsY[i]) && this.originMapCanvas) {
        if (!isDefined(pointsZ) || (isDefined(pointsZ) && isDataValid(pointsZ[i]))) {
          let rgbs = [0, 0, 0];
          if (flag) {
            const LEN = 6;
            let rgb = this.originMapCanvas.ctx.getImageData(
              pointsX[i] * scale + 300 - 3,
              300 - pointsY[i] * scale - 3,
              LEN,
              LEN
            ).data;
            let n = 0;
            for (let i = 0; i < LEN; i++) {
              n++;
              if (rgb[4 * i] === 45 && rgb[4 * i + 1] === 48 && rgb[4 * i + 2] === 57) {
                rgbs[0] = 0;
                rgbs[1] = 0;
                rgbs[2] += 0;
                break;
              } else {
                rgbs[0] += rgb[4 * i];
                rgbs[1] += rgb[4 * i + 1];
                rgbs[2] += rgb[4 * i + 2];
              }
            }
            if (n !== 0) {
              rgbs[0] /= n;
              rgbs[1] /= n;
              rgbs[2] /= n;
            }
          }
          _points.push({
            x: this._dataInMap(pointsX[i]),
            y: this._dataInMap(pointsY[i], true),
            value: isDefined(pointsZ[i]) ? pointsZ[i]._toFixed(fixed) : null,
            rgb: flag ? rgbs : null,
            orginData: {
              x: pointsX[i],
              y: pointsY[i],
              value: mapReference.getPixelIndexAndValue(this.colorMapPtr, pointsX[i], pointsY[i])
            }
          });
        }
      }
    }
    return _points;
  }

  // 绘制瞳孔中心
  _drawPupilEdge() {
    if (this._pupilEdgeValues.length <= 0) return;
    this._ctx.save("translate");
    this._ctx.beginPath();
    this._ctx.moveTo(this._pupilEdgeValues[0].x, this._pupilEdgeValues[0].y);
    this._pupilEdgeValues.map((point, index) => {
      if (index > 0) {
        this._ctx.lineTo(point.x, point.y);
      }
    });
    this._ctx.closePath();
    this._ctx.lineWidth = 2;
    this._ctx.strokeStyle = "white";
    this._ctx.stroke();
    this._ctx.strokeStyle = "black";
    this._ctx.setLineDash([10, 10]);
    this._ctx.stroke();
    this._ctx.restore("translate");
  }

  // 毫米到像素转换
  _dataInMap(v, isY = false) {
    return isY ? -1 * v * this._pixelPerMM : v * this._pixelPerMM;
  }

  // 判断坐标值的合法性
  _isPositionXYValid(...args) {
    for (let i = 0; i < args.length; i++) {
      if (!isDefined(args[i]) || !isDataValid(args[i].value)) return false;
      else return true;
    }
  }
  // 绘制数据点的形状
  _drawShaps() {
    if (!isDefined(this.mapValues)) return;
    this._ctx.save("translate");
    const len = 26;
    let x, y;
    // apex position  -  fill white circle
    if (!isDefined(this.mapValues)) return;
    if (isTrue(this.mapDrawSettings.ShowApexPosition)) {
      // const { PachyApexX, PachyApexY } = this.mapValues
      const { PachyApexX, PachyApexY } = { PachyApexX: { value: 0 }, PachyApexY: { value: 0 } };
      if (this._isPositionXYValid(PachyApexX, PachyApexY)) {
        x = this._dataInMap(PachyApexX.value);
        y = this._dataInMap(PachyApexY.value, true);
        this._ctx.beginPath();
        this._ctx.fillStyle = "white";
        this._ctx.arc(x, y, len / 2, 0, Math.PI * 2);
        this._ctx.fill();
      }
    }
    // min radius front -  fill white diamond
    if (isTrue(this.mapDrawSettings.ShowMinRadiusF)) {
      const { RminX, RminY } = this.mapValues;
      if (this._isPositionXYValid(RminX, RminY)) {
        x = this._dataInMap(RminX.value);
        y = this._dataInMap(RminY.value, true);
        this._ctx.beginPath();
        this._ctx.fillStyle = "white";
        this._ctx.moveTo(-len / 2 + x, y);
        this._ctx.lineTo(x, -len / 2 + y);
        this._ctx.lineTo(len / 2 + x, y);
        this._ctx.lineTo(x, len / 2 + y);
        this._ctx.closePath();
        this._ctx.fill();
      }
    }
    // pupil center - cross
    if (isTrue(this.mapDrawSettings.ShowPupilCenter)) {
      let { PupilCenterX, PupilCenterY } = this.mapValues;
      if (this._isPositionXYValid(PupilCenterX, PupilCenterY)) {
        x = this._dataInMap(PupilCenterX.value);
        y = this._dataInMap(PupilCenterY.value, true);
        this._ctx.beginPath();
        this._ctx.lineWidth = 2;
        this._ctx.moveTo(-len / 2 + x, 0 + y);
        this._ctx.lineTo(len / 2 + x, 0 + y);
        this._ctx.stroke();

        this._ctx.beginPath();
        this._ctx.strokeStyle = "black";
        this._ctx.moveTo(-len / 2 + x + 5, 0 + y);
        this._ctx.lineTo(len / 2 + x - 5, 0 + y);
        this._ctx.stroke();

        this._ctx.beginPath();
        this._ctx.strokeStyle = "white";
        this._ctx.lineWidth = 2;
        this._ctx.moveTo(0 + x, -len / 2 + y);
        this._ctx.lineTo(0 + x, len / 2 + y);
        this._ctx.stroke();

        this._ctx.beginPath();
        this._ctx.strokeStyle = "black";
        this._ctx.moveTo(0 + x, -len / 2 + 5 + y);
        this._ctx.lineTo(0 + x, len / 2 - 5 + y);
        this._ctx.stroke();
      }
    }
    // alpha - black square with white border
    if (isTrue(this.mapDrawSettings.ShowAlpha)) {
      let { LimbusCenterX, LimbusCenterY } = this.mapValues;
      if (this._isPositionXYValid(LimbusCenterX, LimbusCenterY)) {
        x = this._dataInMap(LimbusCenterX.value);
        y = this._dataInMap(LimbusCenterY.value, true);
        this._ctx.beginPath();
        this._ctx.fillStyle = "black";
        this._ctx.fillRect(-len / 2 + x, -len / 2 + y, len, len);
        this._ctx.strokeStyle = "white";
        this._ctx.lineWidth = 2;
        this._ctx.strokeRect(-len / 2 + x, -len / 2 + y, len, len);
      }
    }
    if (this.mapDrawSettings.ShowMaxPeak) {
      if (isDefined(this.maxPeak) && this._isPositionXYValid(this.maxPeak.x, this.maxPeak.y)) {
        x = this._dataInMap(this.maxPeak.x);
        y = this._dataInMap(this.maxPeak.y, true);
        this._ctx.beginPath();
        this._ctx.lineWidth = 4;
        this._ctx.moveTo(-len / 2 + x, 0 + y);
        this._ctx.lineTo(len / 2 + x, 0 + y);
        this._ctx.stroke();

        this._ctx.beginPath();
        this._ctx.lineWidth = 2;
        this._ctx.strokeStyle = "black";
        this._ctx.moveTo(-len / 2 + x, 0 + y);
        this._ctx.lineTo(len / 2 + x, 0 + y);
        this._ctx.stroke();

        this._ctx.beginPath();
        this._ctx.strokeStyle = "white";
        this._ctx.lineWidth = 4;
        this._ctx.moveTo(0 + x, -len / 2 + y);
        this._ctx.lineTo(0 + x, len / 2 + y);
        this._ctx.stroke();

        this._ctx.beginPath();
        this._ctx.strokeStyle = "black";
        this._ctx.lineWidth = 2;
        this._ctx.moveTo(0 + x, -len / 2 + y);
        this._ctx.lineTo(0 + x, len / 2 + y);
        this._ctx.stroke();
      }
    }
    // pupil edge - circle width dash border
    if (this.mapDrawSettings.Show3579Ring) this._draw3579mmRings();
    if (this.mapDrawSettings.ShowPupilEdge) this._drawPupilEdge();
    if (this.mapDrawSettings.ShowDiameter9mm) this._draw9mmSymbol();
    if (this.mapDrawSettings.ShowNasalTemp) this._drawTN();
    if (this.mapDrawSettings.ShowEyeType) this._drawOSOD();
    // this._drawGrid(true)
    if (this.mapDrawSettings.ShowNinemmBorder) this._draw9mmBorder();
    if (this.mapDrawSettings.ShowMaxMin) this._drawMaxMin();
    if (this.mapDrawSettings.ShowNumericValues) this._drawNumericValues();
    this._ctx.restore("translate");
  }

  // 绘制鼠标取点
  _drawTick(e, xy) {
    this._ctx.save("translate");
    if (isDefined(e) && this._isMouseDownBoolen) {
      this._ctx.fillStyle = "black";
      this._ctx.strokeStyle = "black";
      let posX = e.offsetX - this.mapCircle.center.x;
      let posY = e.offsetY - this.mapCircle.center.y;
      let r = this.mapCircle.radius;
      if (isPointInCircle(posX, posY, r)) {
        this._ctx.beginPath();
        this._ctx.moveTo(posX - 10, posY);
        this._ctx.lineTo(posX + 10, posY);
        this._ctx.stroke();
        this._ctx.beginPath();
        this._ctx.moveTo(posX, posY - 10);
        this._ctx.lineTo(posX, posY + 10);
        this._ctx.stroke();
        this._ctx.fillStyle = "white";

        if (isDefined(this.colorMapPtr) && this.colorMapPtr > 0) {
          let result = mapReference.getPixelIndexAndValue(this.colorMapPtr, xy.x, xy.y);
          if (isDefined(result) && isDefined(this.originMapCanvas)) {
            let scalex = (this.originMapCanvas.size.width * 1.0) / this.mapCircle.width;
            let scaley = (this.originMapCanvas.size.height * 1.0) / this.mapCircle.height;
            if (isDefined(this.originMapCanvas) && isDefined(this.originMapCanvas.ctx)) {
              let imageData = this.originMapCanvas.ctx.getImageData(
                (e.offsetX - this.mapCircle.left) * scalex + 10,
                (e.offsetY - this.mapCircle.top) * scaley - 5,
                1,
                1
              ).data;
              this._ctx.fillStyle = `rgb(${255 - imageData[0]},${255 - imageData[1]}, ${255 - imageData[2]})`;
            }
            this._ctx.font = "25px arial";
            let fix = this._mapStyle === MAPSTYLES[5] ? 0 : 2;
            if (result.result === 0 && isDataValid(result.value)) {
              this._ctx.fillText(result.value._toFixed(fix), posX + 10, posY - 5);
              this._ctx.fillStyle = "white";
            }
          }
        }
        let str = `X:${(posX * this._mmPerPixel)._toFixed(2)} , Y:${(-posY * this._mmPerPixel)._toFixed(2)}`;
        if (!this.dataShowSettings.isCartesian) {
          let { dist, angle } = cartesian2polar(posX, -posY);
          str = `${(dist * this._mmPerPixel)._toFixed(2)}mm @ ${angle._toFixed(2)}°`;
        }
        this._ctx.font = this._canvasWidth > 700 ? "18px arial" : "16px arial";
        this._ctx.fillText(str, -this.mapCircle.radius - 20, this.mapCircle.radius + 20);

        this._drawVernier(posX, posY);
      }
    }
    this._ctx.restore("translate");
  }

  // 绘制游标
  _drawVernier(x, y) {
    // y轴游标
    if (this.isRGP) return;
    this._ctx.strokeStyle = "white";
    this._ctx.lineWidth = 2;
    this._ctx.beginPath();
    let offsety = this.canvasSize.width > 700 ? 80 : 50;
    let offsetx = this.canvasSize.width > 700 ? 30 : 30;
    this._ctx.moveTo(-this.mapCircle.radius - offsety, y);
    this._ctx.lineTo(-this.mapCircle.radius - offsety - 5, y);
    this._ctx.stroke();
    // x轴游标
    this._ctx.beginPath();
    this._ctx.moveTo(x, this.mapCircle.radius + offsetx);
    this._ctx.lineTo(x, this.mapCircle.radius + offsetx + 5);
    this._ctx.stroke();
  }

  // 绘制9mm半径图标
  _draw9mmSymbol() {
    this._ctx.save("translate");
    let r = this.mapCircle.radius;
    this._ctx.beginPath();
    this._ctx.lineWidth = 2;
    this._ctx.arc(-r, -r, 8, 0, 2 * Math.PI);
    this._ctx.stroke();
    this._ctx.beginPath();
    this._ctx.lineWidth = 3;
    this._ctx.moveTo(-r + 6, -r + 6);
    this._ctx.lineTo(-r + 13, -r + 13);
    this._ctx.fillText("9mm", -r - 8, -r + 27);
    this._ctx.stroke();
    this._ctx.restore("translate");
  }

  // 绘制鼻侧和颞侧
  _drawTN() {
    this._ctx.save("translate");
    this._ctx.font = "30px arial";
    let TN = this.eyetype === "OS" ? ["N", "T"] : ["T", "N"];
    let r = this.mapCircle.radius;
    this._ctx.fillText(TN[0], -r, r - 10);
    this._ctx.fillText(TN[1], r, r - 10);
    this._ctx.restore("translate");
  }

  // 绘制左右眼
  _drawOSOD() {
    this._ctx.save("translate");
    this._ctx.font = "30px arial";
    let r = this.mapCircle.radius;
    this._ctx.fillText(this.eyetype, r - 50, -r + 15);
    this._ctx.restore("translate");
  }

  // 绘制9mm边界
  _draw9mmBorder() {
    if (this._realDiameter === 9) return;
    this._ctx.save("translate");
    this._ctx.beginPath();
    this._ctx.arc(0, 0, this._pixelPerMM * 4.5, 0, Math.PI * 2);
    this._ctx.stroke();
    this._ctx.strokeStyle = "black";
    this._ctx.beginPath();
    this._ctx.setLineDash([10, 10]);
    this._ctx.arc(0, 0, this._pixelPerMM * 4.5, 0, Math.PI * 2);
    this._ctx.stroke();
    this._ctx.restore("translate");
  }
  _draw3579mmRings() {
    this._ctx.save("translate");
    this._ctx.strokeStyle = "black";
    let arr = [3, 5, 7, 9];
    arr.forEach((e) => {
      this._ctx.beginPath();
      this._ctx.arc(0, 0, (this._pixelPerMM * e) / 2, 0, Math.PI * 2);
      this._ctx.stroke();
    });
    this._ctx.restore("translate");
  }
}



// WEBPACK FOOTER //
// ./src/topography/js/Map.js